import type { Ring, Vec2 } from '../../../../geo/types';
import { horizontalCap, octahedronSolid, planPoint, pyramidOverRing, ringPoints } from '../../solids';
import { rectWall } from '../masonry';
import { pinnacleSolid } from '../gothic/pinnacle';
import { clockDial } from '../gothic/clockDial';
import { parapet, stringCourse } from '../gothic/bands';
import { PINNACLE, pointedAt, type StadhuisBuild } from './stadhuisParts';

/**
 * The tower as the 1544 woodcut draws it (stadhuis-woodcut.jpg): a square shaft with small windows
 * in pairs, a dial under the top, a gallery with corner pinnacles, an octagonal lantern with tall
 * pointed openings and the spire with its finial. Sizes of the openings are not recorded.
 */
const TOWER = {
  windowPairs: [
    { sill: 3.0, top: 5.0 },
    { sill: 7.5, top: 9.5 },
    { sill: 11.5, top: 13.5 },
  ],
  windowWidth: 0.8,
  belfry: { sill: 20.0, top: 23.6, width: 1.1 },
  dial: { y: 17.5, radius: 0.9 },
  gallery: { shaft: 1.4, tip: 3.0, parapet: 0.9, thickness: 0.4 },
  /* Between the storeys of windows, the dial and the belfry, as the woodcut divides the shaft. */
  courses: [6.2, 10.5, 15.0, 19.2],
  course: { height: 0.3, outset: 0.12 },
  lantern: { radiusShare: 0.42, width: 0.7, sillAbove: 0.7, topBelow: 0.5 },
  finial: { rise: 0.3, radius: 0.35 },
} as const;

/* Flat lantern faces toward the Dam and the sides of the shaft. */
const OCTAGON_PHASE = Math.PI / 8;

function rectAt(center: number, width: number, sill: number, top: number): Ring {
  return [
    [center - width / 2, sill],
    [center + width / 2, sill],
    [center + width / 2, top],
    [center - width / 2, top],
  ];
}

/** Two windows in each storey and the two belfry openings. */
function shaftOpenings(side: number): Ring[] {
  const w = TOWER.windowWidth;
  const pairs = TOWER.windowPairs.flatMap((p) => [rectAt(side / 4, w, p.sill, p.top), rectAt((3 * side) / 4, w, p.sill, p.top)]);
  const b = TOWER.belfry;
  const belfry = [pointedAt(side / 4, b.width, b.sill, b.top), pointedAt((3 * side) / 4, b.width, b.sill, b.top)];
  return [...pairs, ...belfry];
}

function buildShaft(s: StadhuisBuild): void {
  const { u0, u1, v0, v1, shaftY } = s.plan.tower;
  const ring: Ring = [[u0, v0], [u1, v0], [u1, v1], [u0, v1]];
  const openings = shaftOpenings(u1 - u0);
  ring.forEach((from, i) => {
    rectWall(s.builder, s.stone, s.frame, [from, ring[(i + 1) % 4]!], [0, shaftY], openings);
  });
  horizontalCap(s.builder, s.stone.wall, s.frame, ring, shaftY, true);
  const dial = { at: [(u0 + u1) / 2, v0] as const, y: TOWER.dial.y, radius: TOWER.dial.radius, outward: [0, -1] as const };
  clockDial(s.builder, { face: s.dark.wall, rim: s.stone.wall }, s.frame, dial);
}

/** The string courses between the storeys. */
function buildCourses(s: StadhuisBuild): void {
  const { course } = TOWER;
  for (const y of TOWER.courses) stringCourse(s.builder, s.stone.wall, s.frame, s.plan.tower, { y, ...course });
}

/** The gallery on the shaft: its parapet and corner pinnacles. */
function buildGallery(s: StadhuisBuild): void {
  const { u0, u1, v0, v1, shaftY } = s.plan.tower;
  const p = PINNACLE;
  const g = TOWER.gallery;
  const wall = { y: shaftY, height: g.parapet, outset: TOWER.course.outset, thickness: g.thickness };
  parapet(s.builder, s.stone.wall, s.frame, s.plan.tower, wall);
  const corners: Vec2[] = [
    [u0 + p.inset, v0 + p.inset],
    [u1 - p.inset, v0 + p.inset],
    [u1 - p.inset, v1 - p.inset],
    [u0 + p.inset, v1 - p.inset],
  ];
  for (const center of corners) {
    const spec = { center, half: p.half, y0: shaftY, shaftTop: shaftY + g.shaft, tipY: shaftY + g.tip };
    pinnacleSolid(s.builder, s.stone.wall, s.frame, spec);
  }
}

/** The octagonal lantern with a pointed opening in every face, the spire and its finial. */
function buildLantern(s: StadhuisBuild): void {
  const { u0, u1, v0, v1, shaftY, octagonY, spireY } = s.plan.tower;
  const center: Vec2 = [(u0 + u1) / 2, (v0 + v1) / 2];
  const l = TOWER.lantern;
  const ring = ringPoints(center, (l.radiusShare * (u1 - u0)) / Math.cos(OCTAGON_PHASE), 8, OCTAGON_PHASE);
  const face = Math.hypot(ring[1]![0] - ring[0]![0], ring[1]![1] - ring[0]![1]);
  const opening = [pointedAt(face / 2, l.width, l.sillAbove, octagonY - shaftY - l.topBelow)];
  ring.forEach((from, i) => {
    rectWall(s.builder, s.stone, s.frame, [from, ring[(i + 1) % 8]!], [shaftY, octagonY], opening);
  });
  pyramidOverRing(s.builder, { roof: s.stone.roof, wall: s.stone.wall }, s.frame, ring, octagonY, { plan: center, y: spireY });
  octahedronSolid(s.builder, s.stone.wall, s.frame, planPoint(center[0], spireY + TOWER.finial.rise, center[1]), TOWER.finial.radius);
}

export function buildStadhuisTower(s: StadhuisBuild): void {
  buildShaft(s);
  buildCourses(s);
  buildGallery(s);
  buildLantern(s);
}
